import IconChevronRight from 'assets/icons/chevron-right-stroke.svg';
import { useResponsiveTruncation } from 'hooks/common/use-responsive-truncation';
import { Gtm } from 'tracking';
import Link from 'next/link';
import { Dispatch, FC, memo, SetStateAction, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getPetProfileUrl } from 'utils/pet-profile/get-pet-profile-url';
import { getPetProfileLabelMessageId } from '../utils/get-pet-profile-label-message-id';
import PetProfileIllustration from '../shared/pet-profile-illustration';
import { isEmptyProfile } from 'stores/pet-profile-new/utils/is-empty-profile';
import { useRootStore } from 'stores/use-root-store';
import { PetType } from 'constants/pet-type';
import { isDogProfile } from 'types/pet-profile';

const PET_TEXT_TRUNCATION_CONFIG = {
  mobile: 10,
  desktop: 13,
} as const;

interface Props {
  setVisibility: Dispatch<SetStateAction<boolean>>;
  petType: PetType;
}

/** Mobile navigation link for the pet profile. */
const PetProfileMobileNavigationLink: FC<Props> = ({ setVisibility, petType }) => {
  const petProfile = useRootStore((store) => store.currentPetProfile);
  const { formatMessage } = useIntl();
  const getTruncatedText = useResponsiveTruncation(PET_TEXT_TRUNCATION_CONFIG);

  const labelMessageId = getPetProfileLabelMessageId(petProfile, petType);
  const label = getTruncatedText(
    labelMessageId ? formatMessage({ id: labelMessageId }) : (petProfile.name ?? ''),
    true
  );

  const handlePetProfileClick = (): void => {
    setVisibility(false);

    Gtm.track.selectElement({
      element:
        petType === PetType.Dog
          ? ['Navigation', 'Hunde', 'Pet Profile']
          : ['Navigation', 'Katzen', 'Pet Profile'],
    });
  };

  const href = useMemo(() => {
    return getPetProfileUrl({ petType, params: {} });
  }, [petType]);

  return (
    <Link
      href={href}
      className="border-grey-300 relative flex items-center gap-1.5 border-b px-4 py-3 leading-none"
      onClick={() => handlePetProfileClick()}
      data-test="pet-profile-navigation-link"
    >
      <PetProfileIllustration
        isEmpty={isEmptyProfile(petProfile)}
        breed={isDogProfile(petProfile) ? petProfile.selectedBreed : undefined}
        petType={petType}
        className="h-8 w-8"
        imageSize={32}
        data-test="pet-profile-navigation-illustration"
      />
      <span className="text-primary-main text-sm leading-4 font-black" data-test="pet-profile-name">
        {label}
      </span>
      <IconChevronRight
        className="text-primary-main ml-auto h-8 w-6"
        data-test="pet-profile-chevron-icon"
      />
    </Link>
  );
};

export default memo(PetProfileMobileNavigationLink);
